import React, { useState, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { motion, AnimatePresence } from "framer-motion";

import { useAtom } from "jotai";
import {
  themeAtom,
  studiosAtom,
  calendarColoursAtom,
  currentStaffDataAtom,
} from "../../../../-Atoms";
import { db, updateDoc, doc, httpsCallable, functions } from "../../../../firebaseConfig";

const CalendarSelector = ({ jobData, isEventTomorrow }) => {
  const [theme] = useAtom(themeAtom);
  const [studios] = useAtom(studiosAtom);
  const [calendarColours] = useAtom(calendarColoursAtom);
  const [currentStaffData] = useAtom(currentStaffDataAtom);

  const [studio, setStudio] = useState(jobData?.studio || "");
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setStudio(jobData?.studio || "");
  }, [jobData]);

  const handleStudioChange = (event) => {
    if (!event || !event.target) {
      console.error("Invalid event object in handleStudioChange", event);
      return;
    }

    if (!jobData || !jobData.id) {
      console.error("Invalid jobData or missing jobData.id", jobData);
      return;
    }

    const newStudio = event.target.value;
    setMenuOpen(false);

    setStudio(newStudio);

    updateDoc(doc(db, "JobData", jobData.id), { studio: newStudio })
      .then(() => {
        const oldStudio = jobData.studio;
        if (oldStudio !== newStudio) {
          sendStudioChangedNotification(
            jobData.client,
            jobData.jobTypeFull,
            isEventTomorrow,
            newStudio,
            jobData
          );
        }
      })
      .catch((error) => {
        console.error("Error updating studio:", error);
        setStudio(jobData.studio || "");
      });
  };

  const sendNotificationToUID = httpsCallable(functions, "sendNotificationToUID");

  const sendStudioChangedNotification = (
    client,
    jobType,
    isEventTomorrow,
    newStudio,
    jobData
  ) => {
    if (!isEventTomorrow(new Date(jobData.bookedStart).toISOString())) return;
    if (!sendNotificationToUID) return;
    try {
      const notificationData = {
        title: `STUDIO CHANGED - ${client} - ${jobType.replace(/\(.*\)/, "")}`,
        text: `The studio for your job tomorrow has been changed to ${newStudio}, please tap to check the calendar.`,
        jumpto: new Date(jobData.bookedStart).getTime(),
        kind: "notificationNextDayChangesMe",
      };

      const staffIds = new Set();

      if (jobData.photographer) staffIds.add(jobData.photographer);
      if (jobData.operator) staffIds.add(jobData.operator);
      if (jobData.stylist) staffIds.add(jobData.stylist);

      jobData.additionalPhotographers &&
        jobData.additionalPhotographers.forEach((id) => id && staffIds.add(id));
      jobData.additionalOperators &&
        jobData.additionalOperators.forEach((id) => id && staffIds.add(id));
      jobData.additionalStylists &&
        jobData.additionalStylists.forEach((id) => id && staffIds.add(id));

      staffIds.forEach((staffId) => {
        if (staffId) {
          sendNotificationToUID({
            ...notificationData,
            id: staffId,
          });
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  const getCurrentStudioColor = () => {
    if (!studio) return "#888888";

    return theme === "dark"
      ? calendarColours[studio]?.darkBackground?.replace(
          /(#(?:[\da-fA-F]{6}|[\da-fA-F]{3}))[\da-fA-F]{2}/,
          "$1"
        ) || "#888888"
      : calendarColours[studio]?.background || "#888888";
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    }),
    hover: { scale: 1.03 },
    tap: { scale: 0.98 },
  };

  const colorIndicatorVariants = {
    normal: { scale: 1 },
    hover: { scale: 1.15, rotate: 5 },
    selected: { scale: 1.1, rotate: 0 },
  };

  // Ensure the studio value is valid or fallback to empty string
  const selectValue = studios.includes(studio) ? studio : "";

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 1,
        position: "relative",
        "& .MuiInputLabel-root": {
          color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          fontWeight: 500,
          fontSize: "0.95rem",
          transition: "all 0.2s ease",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, 7px) scale(0.75)",
          },
        },
        "& .MuiInputLabel-filled": {
          transform: "translate(14px, 16px) scale(1)",
        },
        "& .MuiFilledInput-root": {
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(240,240,240,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          "&:hover": {
            backgroundColor:
              theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
            boxShadow:
              theme === "dark"
                ? `0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)`
                : `0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)`,
          },
          "&.Mui-focused": {
            boxShadow:
              theme === "dark"
                ? `0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)`
                : `0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)`,
          },
        },
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          padding: "24px 14px 8px 14px",
        },
      }}
      variant="filled"
    >
      <InputLabel id="studio-calendar">Studio / Calendar</InputLabel>
      <Select
        disableUnderline
        labelId="studio-calendar-select"
        id="studio-calendar-select"
        value={selectValue}
        label="Studio / Calendar"
        error={!studio}
        onChange={handleStudioChange}
        open={open}
        onOpen={() => {
          setOpen(true);
          setMenuOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          setMenuOpen(false);
          setHoverIndex(null);
        }}
        renderValue={(selected) => {
          if (!selected) return "";
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <motion.div
                initial={false}
                animate={{
                  backgroundColor: getCurrentStudioColor(),
                  boxShadow: `0 0 10px ${getCurrentStudioColor()}80`,
                }}
                transition={{ duration: 0.3 }}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "5px",
                  display: "inline-block",
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: theme === "dark" ? "#ffffff" : "#000000",
                }}
              >
                {selected}
              </Typography>
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor:
                theme === "dark" ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)",
              "& .MuiMenuItem-root": {
                padding: "10px 16px",
                margin: "1px 3px",
                borderRadius: "8px",
              },
              "& .MuiList-root": {
                padding: "2px",
              },
            },
          },
          TransitionProps: {
            timeout: 100,
          },
        }}
      >
        <AnimatePresence>
          {studios.map((s, index) => {
            const calendarColor =
              theme === "dark"
                ? calendarColours[s]?.darkBackground?.replace(
                    /(#(?:[\da-fA-F]{6}|[\da-fA-F]{3}))[\da-fA-F]{2}/,
                    "$1"
                  ) || "#888888"
                : calendarColours[s]?.background || "#888888";

            const isSelected = studio === s;
            const isHovered = hoverIndex === index;

            return (
              <MenuItem
                key={s}
                value={s}
                onMouseEnter={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex(null)}
                onClick={(e) => {
                  e.stopPropagation();

                  handleStudioChange({
                    target: { value: s },
                  });

                  setOpen(false);
                  setMenuOpen(false);
                }}
                sx={{
                  backgroundColor: isSelected
                    ? theme === "dark"
                      ? "rgba(100,100,255,0.2)"
                      : "rgba(100,100,255,0.15)"
                    : "transparent",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? theme === "dark"
                        ? "rgba(100,100,255,0.3)"
                        : "rgba(100,100,255,0.25)"
                      : theme === "dark"
                      ? "rgba(60,60,60,0.5)"
                      : "rgba(240,240,240,0.7)",
                  },
                }}
              >
                <motion.div
                  custom={index}
                  initial={menuOpen ? "hidden" : "visible"}
                  animate="visible"
                  variants={itemVariants}
                  whileHover="hover"
                  whileTap="tap"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    padding: "2px 0",
                  }}
                >
                  <Tooltip
                    title={`Calendar color: ${calendarColor}`}
                    placement="left"
                    arrow
                  >
                    <Box
                      component={motion.div}
                      initial="normal"
                      animate={
                        isSelected ? "selected" : isHovered ? "hover" : "normal"
                      }
                      variants={colorIndicatorVariants}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 15,
                      }}
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "6px",
                        backgroundColor: calendarColor,
                        marginRight: 2,
                        boxShadow:
                          isSelected || isHovered
                            ? `0 0 12px ${calendarColor}90, 0 0 0 1px ${calendarColor}50`
                            : `0 0 8px ${calendarColor}70, 0 0 0 1px ${calendarColor}30`,
                        display: "inline-block",
                      }}
                    />
                  </Tooltip>
                  <Box
                    component={motion.span}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontWeight: isSelected ? 600 : isHovered ? 500 : 400,
                      letterSpacing: isSelected ? "0.01em" : "normal",
                      color: isSelected
                        ? theme === "dark"
                          ? "#ffffff"
                          : "#000000"
                        : theme === "dark"
                        ? "rgba(255,255,255,0.85)"
                        : "rgba(0,0,0,0.85)",
                    }}
                  >
                    {s}
                  </Box>
                </motion.div>
              </MenuItem>
            );
          })}
        </AnimatePresence>
      </Select>
    </FormControl>
  );
};

export default CalendarSelector;